import React, { useCallback } from 'react';
import { FixedSizeList as List } from 'react-window';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Checkbox, useMediaQuery, useTheme } from '@mui/material';
import { IGroupObject } from 'src/models/objectManagement';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

interface Props {
  groupObjects: Array<IGroupObject>;
  loading?: boolean;
  // groupSelectedById: string | undefined;
  onClick: (group: IGroupObject) => void;
  initialSelectedGroups: IGroupObject[];
  showDateAndObjectCount?: boolean;
}

const ROW_HEIGHT = 60;
const HEADER_HEIGHT = 56;
const TABLE_WIDTH = 780;

type ColumnAlignment = 'inherit' | 'left' | 'center' | 'right' | 'justify';
type ColumnWidth = number | 'auto';

interface ColumnConfig {
  id: string;
  width: ColumnWidth;
  label?: string;
  align?: ColumnAlignment;
}

const COLUMNS: readonly ColumnConfig[] = [
  { id: 'radio', width: 50 },
  { id: 'group', width: 500, label: 'group' },
  { id: 'added', width: 115, label: 'added', align: 'center' },
  { id: 'count', width: 115, label: 'object count', align: 'center' }
] as const;

interface VirtualizedTableBodyProps {
  items: Array<IGroupObject>;
  // groupSelectedById: string | undefined;
  onRowClick: (group: IGroupObject) => void;
  selectedGroups: IGroupObject[];
  showDateAndObjectCount: boolean;
}

const VirtualizedTableBody: React.FC<VirtualizedTableBodyProps> = ({
  items,
  selectedGroups,
  onRowClick,
  showDateAndObjectCount
}) => {
  const theme = useTheme();
  const { groupObject } = useAppSelector((state) => state.objectManagement);
  const matchesUpMd = useMediaQuery(theme.breakpoints.up('md'));

  const Row = useCallback(
    ({ index, style }: { index: number; style: React.CSSProperties }) => {
      const row = items[index];

      const isGroupSelected = () => {
        if (row.id === 'all')
          return selectedGroups.length === groupObject.data.length;

        return selectedGroups.some((group) => group?.id === row.id);
      };

      const isIndeterminate = () => {
        if (row.id === 'all') {
          return (
            selectedGroups.length > 0 &&
            selectedGroups.length < groupObject.data.length
          );
        }
        return false;
      };

      return (
        <TableRow
          hover
          onClick={() => onRowClick(row)}
          style={{
            ...style,
            display: 'table',
            tableLayout: 'fixed',
            width: `${TABLE_WIDTH}px`,
            ...(isGroupSelected()
              ? {
                backgroundColor: theme.colors.primary.light
              }
              : {})
          }}
        >
          <TableCell width={showDateAndObjectCount ? COLUMNS[0].width : '10%'}>
            <Checkbox
              checked={isGroupSelected()}
              indeterminate={isIndeterminate()}
              value={row.id}
              name="group-object"
              size="small"
              sx={{ color: theme.palette.primary.main }}
            />
          </TableCell>
          <TableCell
            width={showDateAndObjectCount ? COLUMNS[1].width : '90%'}
            sx={{
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              lineHeight: 1.3
            }}
          >
            {!matchesUpMd && row.displayName?.length > 35
              ? row.displayName?.slice(0, 35) + '...'
              : row.displayName}
          </TableCell>
          {showDateAndObjectCount && (
            <>
              <TableCell width={COLUMNS[2].width} align="center">
                {row.createdAt ? moment(row.createdAt).format('D/M/YY') : ''}
              </TableCell>
              <TableCell width={COLUMNS[3].width} align="center">
                {row.id === 'all' ? '' : row.objectList.length}
              </TableCell>
            </>
          )}
        </TableRow>
      );
    },
    [items, selectedGroups, onRowClick, theme.palette]
  );

  return (
    <List
      height={parseInt(theme.spacing(30)) - HEADER_HEIGHT}
      itemCount={items.length}
      itemSize={ROW_HEIGHT}
      width={TABLE_WIDTH}
      style={{ overflowX: 'hidden' }}
    >
      {Row}
    </List>
  );
};

const TableGroupObject: React.FC<Props> = ({
  groupObjects,
  loading = false,
  // groupSelectedById,
  onClick,
  initialSelectedGroups,
  showDateAndObjectCount = true
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const handleClick = (group: IGroupObject) => {
    onClick(group);
  };

  const matchesUpMd = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <TableContainer
      component={Paper}
      sx={{
        width: 'fit-content',
        maxWidth: '100%',
        overflowX: 'hidden'
      }}
    >
      <Table
        style={{
          tableLayout: 'fixed',
          minWidth: `${TABLE_WIDTH}px`,
          width: `${TABLE_WIDTH}px`
        }}
      >
        <TableHead>
          <TableRow
            style={{
              display: 'table',
              width: `${TABLE_WIDTH}px`,
              tableLayout: 'fixed'
            }}
          >
            {COLUMNS.map((column) => (
              <TableCell
                key={column.id}
                align={column.align}
                width={column.width !== 'auto' ? column.width : undefined}
              >
                {column.label ? t(column.label) : ''}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <VirtualizedTableBody
            items={groupObjects}
            // groupSelectedById={groupSelectedById}
            onRowClick={handleClick}
            selectedGroups={initialSelectedGroups}
            showDateAndObjectCount={showDateAndObjectCount}
          />
        </TableBody>
      </Table>
    </TableContainer>
  );
};

interface IPropsTableGroupObjectWithCheckbox {
  groupObjects: Array<IGroupObject>;
  selectedGroups: IGroupObject[];
  setSelectedGroups: React.Dispatch<React.SetStateAction<IGroupObject[]>>;
  showDateAndObjectCount: boolean;
}

const TableGroupObjectWithCheckbox: React.FC<IPropsTableGroupObjectWithCheckbox> =
  ({
    groupObjects,
    selectedGroups,
    setSelectedGroups,
    showDateAndObjectCount
  }) => {
    const theme = useTheme();

    const handleClick = (group: IGroupObject) => {
      const isGroupSelected = selectedGroups.some((g) => g.id === group.id);

      if (isGroupSelected) {
        setSelectedGroups(selectedGroups.filter((g) => g.id !== group.id));
      } else {
        setSelectedGroups([...selectedGroups, group]);
      }
    };

    // Hitung jumlah item yang dipilih
    const numSelected = selectedGroups.length;
    const rowCount = groupObjects.length;

    const matchesUpMd = useMediaQuery(theme.breakpoints.up('md'));

    return (
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: theme.spacing(30),
          width: 'fit-content',
          maxWidth: '100%',
          overflowX: 'hidden'
        }}
      >
        <Table
          style={{
            tableLayout: 'fixed',
            minWidth: `${TABLE_WIDTH}px`,
            width: `${TABLE_WIDTH}px`
          }}
        >
          <TableBody>
            <VirtualizedTableBody
              items={groupObjects}
              onRowClick={handleClick}
              selectedGroups={selectedGroups}
              showDateAndObjectCount={showDateAndObjectCount}
            />
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

export { TableGroupObjectWithCheckbox };
export default TableGroupObject;
